import React, { useState, memo, useRef, useLayoutEffect } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import type { Modifier } from '@dnd-kit/core';
import {
  restrictToWindowEdges,
  snapCenterToCursor,
} from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { Ionicons } from '@expo/vector-icons';
import { GroceryDraggableListProps, DraggableGroceryItem } from './GroceryDraggableList.types';

/** Restrict overlay horizontal movement to a viewport rect (e.g. content area when sidebar is visible). Y is left unrestricted so the overlay follows the pointer vertically. */
function createRestrictToRectHorizontal(
  boundingRect: { left: number; top: number; width: number; height: number }
): Modifier {
  return ({ transform, draggingNodeRect }) => {
    if (!draggingNodeRect) return transform;
    const value = { ...transform };
    const rect = draggingNodeRect;
    const { left, width } = boundingRect;
    const right = left + width;
    if (rect.left + transform.x < left) value.x = left - rect.left;
    else if (rect.right + transform.x > right) value.x = right - rect.right;
    return value;
  };
}

/** Pointer-based detection (reliable when scrolled); fallback to rect intersection when pointer is between items */
function sortableCollisionDetection(args: Parameters<typeof pointerWithin>[0]) {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) return pointerCollisions;
  return rectIntersection(args);
}

const SortableItem = memo(function SortableItem({
  item,
  renderItem,
}: {
  item: DraggableGroceryItem;
  renderItem: GroceryDraggableListProps['renderItem'];
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
    // Promote to own layer during drag to reduce repaints and improve smoothness
    willChange: isDragging ? 'transform' : undefined,
  };

  const dragHandle = (
    <div
      {...listeners}
      {...attributes}
      style={{
        cursor: 'grab',
        touchAction: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '8px',
      }}
    >
      <Ionicons name="reorder-three-outline" size={22} color="#6B7280" />
    </div>
  );

  return (
    <div ref={setNodeRef} style={style}>
      {renderItem(item, dragHandle)}
    </div>
  );
});

export default function GroceryDraggableList({
  uncheckedItems,
  checkedItems,
  onReorder,
  renderItem,
  ListHeaderComponent: _ListHeaderComponent,
  ListFooterComponent: _ListFooterComponent,
  contentContainerStyle: _contentContainerStyle,
  dragOverlayBounds,
}: GroceryDraggableListProps) {
  const [activeItem, setActiveItem] = useState<DraggableGroceryItem | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [measuredBounds, setMeasuredBounds] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  const measureContainer = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMeasuredBounds({
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    });
  }, []);

  const setContainerRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      if (el) measureContainer();
    },
    [measureContainer]
  );

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const ro = new ResizeObserver(measureContainer);
    const el = containerRef.current;
    if (el) ro.observe(el);
    window.addEventListener('resize', measureContainer);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureContainer);
    };
  }, [measureContainer]);

  const overlayModifiers = React.useMemo(() => {
    const bounds = measuredBounds ?? dragOverlayBounds;
    return bounds
      ? [snapCenterToCursor, createRestrictToRectHorizontal(bounds), restrictToWindowEdges]
      : [snapCenterToCursor, restrictToWindowEdges];
  }, [measuredBounds, dragOverlayBounds]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const item = uncheckedItems.find((i) => i.id === event.active.id);
    if (item) setActiveItem(item);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveItem(null);
    if (!over || active.id === over.id) return;
    const oldIndex = uncheckedItems.findIndex((i) => i.id === active.id);
    const newIndex = uncheckedItems.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(uncheckedItems, oldIndex, newIndex).map((item, index) => ({
      ...item,
      sort_order: index,
    }));
    onReorder(reordered);
  };

  return (
    <div ref={setContainerRef} style={{ minHeight: 0 }}>
      <DndContext
        sensors={sensors}
        collisionDetection={sortableCollisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={uncheckedItems.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          {uncheckedItems.map((item) => (
            <SortableItem key={item.id} item={item} renderItem={renderItem} />
          ))}
        </SortableContext>

        <DragOverlay
        modifiers={overlayModifiers}
        dropAnimation={{
          duration: 200,
          easing: 'cubic-bezier(0.18, 0.67, 0.6, 1)',
        }}
      >
        {activeItem ? (
          <div
            style={{
              pointerEvents: 'none',
              opacity: 0.98,
              willChange: 'transform',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              borderRadius: 8,
            }}
          >
            {renderItem(activeItem)}
          </div>
        ) : null}
      </DragOverlay>

      {checkedItems.length > 0 && (
        <div>
          {checkedItems.map((item) => (
            <div key={item.id}>{renderItem(item)}</div>
          ))}
        </div>
      )}
    </DndContext>
    </div>
  );
}
